import * as React from 'react';

import type { DescriptionPage_ProductVariant as DescriptionPageProductVariant } from '__generated__/graphql-types';
import classNames from 'classnames';
import omit from 'lodash/omit';

import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';
import store from 'js/lib/coursera.store';
import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import { HAS_PREVIOUSLY_LOGGED_IN } from 'bundles/authentication-common/constants';
import useIsInLegalConsolidationFlowAndHasTouQueryParam from 'bundles/authentication-common/hooks/useIsInLegalConsolidationFlowAndHasTouQueryParam';
import GetS12nCertificateModal from 'bundles/enroll-common/components/common/GetS12nCertificateModal';
import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { ENROLL } from 'bundles/enroll-common/utils/enrollActionParams';
import EnrollModal from 'bundles/enroll-modal/components/EnrollModal';
import PreloadEnrollModal from 'bundles/enroll-modal/components/PreloadEnrollModal';
import { useEnrollModalExperiments } from 'bundles/enroll-modal/hooks/useEnrollModalExperiments';
import { MODAL_TYPES, useEnrollmentModalState } from 'bundles/enroll-modal/hooks/useEnrollmentModalState';
import { useEnrollmentTracking } from 'bundles/enroll-modal/hooks/useEnrollmentTracking';
import { useOpenAidModal } from 'bundles/enroll-modal/hooks/useOpenAidModal';
import { getEnrollmentRouting } from 'bundles/enroll-modal/utils/enrollModalUtils';
import type { CourseType } from 'bundles/enterprise-legacy-learner-home/utils/courseTypeMetadataUtils';
import OpenAidApplicationModalV2 from 'bundles/finaid-common/components/OpenAidApplicationModalV2';
import type { FinancialAidApplicationsWithUserAndProducts_FinancialAidApplicationsV2Resource_byUserAndProductIds_elements as FinancialAidApplication } from 'bundles/finaid-common/enhancers/__generated__/FinancialAidApplicationsWithUserAndProducts';
import type { CourseTypeMetadataV1 } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import type PromotionEligibilitiesV1 from 'bundles/naptimejs/resources/promotionEligibilities.v1';
import type S12nDerivativesV1 from 'bundles/naptimejs/resources/s12nDerivatives.v1';
import TopLevelModal from 'bundles/phoenix/components/TopLevelModal';
import type { S12nProductVariant } from 'bundles/s12n-common/constants/s12nProductVariants';

type OpenFinAidInfo = Partial<FinancialAidApplication> & {
  courseName: string;
  courseId: string;
};

export type PropsFromCaller = {
  courseId: string;
  course?: CoursesV1;
  isEnrolled: boolean;
  isSpecialization?: boolean;
  isEligibleForPlusMonthlyToAnnualUpgrade: boolean;
  isCourseraPlusEligibleProduct?: boolean;
  showUndoCancellationCTA?: boolean;
  isS12nUpgradeEligible?: boolean;
  s12nId?: string;
  currentS12nId?: string;
  name?: string;
  slug?: string;
  courseType?: CourseType;
  s12nProductVariant?: DescriptionPageProductVariant | S12nProductVariant;
  hideComponent?: boolean;
  additionalQueryParams?: { [key: string]: string };
  shouldCheckActionParams?: boolean;
  shouldCheckProductIdParam?: boolean;
  enrollmentAvailableChoices?: EnrollmentChoices;
  s12nDerivative?: S12nDerivativesV1;
  promotionEligibilities?: PromotionEligibilitiesV1;
  router: InjectedRouter;
  trackingName?: string;
  rootClassName?: string;
  // return `true` to skip the normal enroll flow (e.g. go straight to checkout instead of the modal).
  // Receives the resolved enrollment choices so callers can drive checkout without re-fetching them.
  alternateEnrollFlow?: (enrollmentAvailableChoices?: EnrollmentChoices) => boolean;
  ctaBottom?: string | JSX.Element;
  showOpenAidModal?: boolean;
  openFinAidInfo?: OpenFinAidInfo;
  productVariant?: string;
  subtitleElem?: React.ReactNode;
  onClick?: () => void;
  buttonTrackingName?: string;
  partnerIds?: Array<string>;
  lastActiveCourseSlug?: string;
  courseTypeMetadataWithVersion?: CourseTypeMetadataV1;
};

type ExtraProps = {
  onClick: () => void;
  onMultipleLearningProgramsLinkClick?: () => void;
};

function withEnrollModal<Props extends PropsFromCaller>(
  WrappedComponent: React.ComponentType<Props & ExtraProps>
): React.ComponentType<Props> {
  const WithEnrollModal = (props: Props) => {
    const {
      isEnrolled,
      slug,
      router,
      additionalQueryParams,
      shouldCheckActionParams,
      shouldCheckProductIdParam,
      enrollmentAvailableChoices,
      courseType,
      s12nProductVariant,
      name,
      courseId,
      course,
      s12nId,
      currentS12nId,
      isS12nUpgradeEligible = false,
      trackingName,
      isSpecialization,
      alternateEnrollFlow,
      showOpenAidModal,
      productVariant,
      openFinAidInfo,
      hideComponent,
      rootClassName,
      ctaBottom,
      partnerIds,
      lastActiveCourseSlug,
      courseTypeMetadataWithVersion,
    } = props;

    const {
      activeModal,
      toggleOpenAidModal,
      toggleEnrollModal,
      toggleGetS12nCertificateModal,
      fromMultipleLearningProgramsLink,
      setFromMultipleLearningProgramsLink,
    } = useEnrollmentModalState();
    const { trackEnroll, trackEnrollButtonClick } = useEnrollmentTracking({
      courseId,
      s12nId,
      name,
      isSpecialization,
      courseType,
      s12nProductVariant,
      router,
    });
    const { openAidModalType, canEnrollThroughPreview, isPreviewLoading, handleClickPreview } = useOpenAidModal({
      courseId,
      course,
      enrollmentAvailableChoices,
      courseTypeMetadataWithVersion,
      productVariant,
      isSpecialization,
      s12nId,
      toggleEnrollModal,
    });
    const isLegalConsolidationFlowAndHasTouQueryParam = useIsInLegalConsolidationFlowAndHasTouQueryParam();
    const isAnAuthenticatedUser = user.isAuthenticatedUser();
    const retrackedTrack = useRetracked();

    const { shouldPreloadEnrollModal } = useEnrollModalExperiments({
      activeModal,
      enrollmentAvailableChoices,
      courseId,
      s12nId,
    });

    const { shouldAutoOpenEnrollOnMount, courseHomeRedirectUrl } = getEnrollmentRouting({
      courseId,
      s12nId,
      slug,
      course,
      isEnrolled,
      isSpecialization,
      isS12nUpgradeEligible,
      isLegalConsolidationFlowAndHasTouQueryParam,
      shouldCheckActionParams,
      shouldCheckProductIdParam,
      lastActiveCourseSlug,
      router,
    });

    const showEnrollmentChoice = () => {
      // Three possibilities here:
      //  1. alternateEnrollFlow doesn't exist: the call "returns" undefined and we continue with showEnrollmentChoice
      //  2. alternateEnrollFlow exists and returns false: alternateEnrollFlow is called and we continue with
      //     showEnrollmentChoice
      //  3. alternateEnrollFlow exists and returns true: alternateEnrollFlow is called and we SKIP
      //     showEnrollmentChoice (alternateEnrollFlow replaces the normal enroll flow)
      // Example use-case for #3 would be to scroll the page to the Enrollment Options section INSTEAD OF showing the
      // Enroll modal.
      if (showOpenAidModal) {
        toggleOpenAidModal();
        return;
      }

      if (alternateEnrollFlow?.(enrollmentAvailableChoices)) {
        return;
      }

      toggleEnrollModal();
    };

    const handleNormalEnrollmentFlow = () => {
      const hasEarnedS12nCert = enrollmentAvailableChoices?.hasEarnedS12nCertificate;
      const preEnrollEnabledButNotEligible = isSpecialization
        ? enrollmentAvailableChoices?.isNotEligibleToPreEnroll
        : enrollmentAvailableChoices?.isNotEligibleToPreEnroll &&
          enrollmentAvailableChoices?.hasOnlyPreEnrollEnabledS12ns;

      if (!isAnAuthenticatedUser) {
        const hasPreviouslyLoggedIn = store.get(HAS_PREVIOUSLY_LOGGED_IN);

        const authModeValue = hasPreviouslyLoggedIn ? 'login' : 'signup';
        router.replace({
          pathname: router.location.pathname,
          query: omit(
            {
              ...router.location.query,
              ...additionalQueryParams,
              action: ENROLL,
              ...(shouldCheckProductIdParam ? { productId: isSpecialization ? s12nId : courseId } : {}),
            },
            'authMode'
          ),
          hash: buildHashWithAuthMode(getLocationHash(router.location), authModeValue),
        });

        if (hasPreviouslyLoggedIn) {
          retrackedTrack({
            trackingName: HAS_PREVIOUSLY_LOGGED_IN,
            action: 'show',
            trackingData: {},
          });
        }
      } else if (courseHomeRedirectUrl) {
        redirect.setLocation(courseHomeRedirectUrl);
      } else if (hasEarnedS12nCert) {
        toggleGetS12nCertificateModal();
      } else if (preEnrollEnabledButNotEligible) {
        // If this is the XDP for something that is pre enroll enabled but not eligible to pre-enroll, we shouldn't open
        // any modals and should thus do nothing. This can be removed once pre-enrollment is fully rolled out
      } else {
        trackEnroll();

        showEnrollmentChoice();
      }
    };

    const _onClick = () => {
      const trackingData = {
        s12nId,
        courseId,
        path: window.location.pathname,
        isAuthenticatedUser: isAnAuthenticatedUser,
      };

      if (trackingName) {
        retrackedTrack({
          trackingName,
          action: 'click',
          trackingData,
        });
      }

      handleNormalEnrollmentFlow();
    };

    const onClick = () => {
      // We want to track all enroll button clicks, even for logged out users
      trackEnrollButtonClick();

      // For all other clicks, we should make sure that the openS12nSelectModal is set to false
      setFromMultipleLearningProgramsLink(false);
      _onClick();
    };

    const onMultipleLearningProgramsLinkClick = () => {
      // This is specifically used for pre enrollment when the multiple learning programs link
      // is clicked that we open up specifically the s12n select modal. The link only
      // shows up for mix-and-match cases and should always open up the s12n select modal
      setFromMultipleLearningProgramsLink(true);
      _onClick();
    };

    React.useEffect(() => {
      if (shouldAutoOpenEnrollOnMount) {
        onClick();
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLegalConsolidationFlowAndHasTouQueryParam]);

    const enrollModalProps =
      isSpecialization && s12nId
        ? { s12nId, courseId, partnerIds, onSdp: true, courseTypeMetadataWithVersion }
        : { s12nId, courseId, partnerIds, fromMultipleLearningProgramsLink, courseTypeMetadataWithVersion };

    const renderModal = () => {
      switch (activeModal) {
        case MODAL_TYPES.ENROLL:
          return (
            <TopLevelModal>
              <EnrollModal {...enrollModalProps} router={router} onClose={toggleEnrollModal} />
            </TopLevelModal>
          );
        case MODAL_TYPES.GET_CERTIFICATE:
          if (s12nId) {
            return <GetS12nCertificateModal s12nId={s12nId} onClose={toggleGetS12nCertificateModal} />;
          } else {
            throw new Error('Expect MODAL_TYPES.GET_CERTIFICATE to have a valid s12nId');
          }
        case MODAL_TYPES.OPEN_AID:
          return (
            <OpenAidApplicationModalV2
              type={openAidModalType}
              approvalDate={openFinAidInfo?.approvalDate}
              cartId={openFinAidInfo?.cartId}
              applicationState={openFinAidInfo?.state}
              handleClose={toggleOpenAidModal}
              handlePreview={handleClickPreview}
              isPreviewLoading={isPreviewLoading}
              isPreviewAvailable={canEnrollThroughPreview}
              handleCancelAndEnroll={toggleEnrollModal}
            />
          );
        default:
          return null;
      }
    };

    if (hideComponent) {
      return null;
    }

    const s12nUpgradeLink = isS12nUpgradeEligible ? `/learn/specialization/upgrade/${currentS12nId}:${s12nId}` : '';

    return (
      <div className={classNames('with-enroll-modal', rootClassName)}>
        {renderModal()}
        {shouldPreloadEnrollModal ? <PreloadEnrollModal {...enrollModalProps} /> : null}
        <WrappedComponent
          {...props}
          ctaBottom={ctaBottom}
          onClick={onClick}
          onMultipleLearningProgramsLinkClick={onMultipleLearningProgramsLinkClick}
          s12nUpgradeLink={s12nUpgradeLink}
        />
      </div>
    );
  };

  return WithEnrollModal;
}

export default withEnrollModal;
